import React, { useContext, useMemo } from 'react'
import {
  useUserNotificationsChannels,
  useUserNotificationsListTable,
  NotificationChannel,
  UserNotification,
} from '@/services/NotificationsService'

interface NotificationsContextProps {
  notifications?: UserNotification[]
  isNotificationsLoading: boolean
  canFetchMore?: boolean | null
  notificationChannels: NotificationChannel[]
  totalNotificationsCount: number
  channelList: string[]
  fetchMoreNotifications: (input?: { where?: { channelId?: string | null } | null }) => Promise<UserNotification[]>
  resetNotifications: (input?: { where?: { channelId?: string | null } | null }) => Promise<UserNotification[]>
}

interface NotificationsProviderProps {
  children: React.ReactNode
}

const defaultFetchMore = async () => [] as UserNotification[]

const NotificationsContext = React.createContext<NotificationsContextProps>({
  isNotificationsLoading: false,
  canFetchMore: false,
  notificationChannels: [],
  channelList: [],
  totalNotificationsCount: 0,
  fetchMoreNotifications: defaultFetchMore,
  resetNotifications: defaultFetchMore,
})

export const NotificationsProvider = ({ children }: NotificationsProviderProps) => {
  const { notificationChannels } = useUserNotificationsChannels()
  const { notifications, loading, canFetchMore, fetchMoreNotifications, resetNotifications, totalNotificationsCount } =
    useUserNotificationsListTable({
      cursor: '',
      take: 20,
    })

  const channelList = useMemo(() => {
    const channelArray = ['All']
    const notificationsArray = notificationChannels?.map((channel) => channel?.displayName)
    return notificationChannels ? channelArray.concat(notificationsArray as []) : []
  }, [notificationChannels])

  const updatedNotifications = notifications as UserNotification[]
  const updatedNotificationChannels = notificationChannels as NotificationChannel[]

  const contextValue = useMemo(() => {
    return {
      notifications: updatedNotifications,
      isNotificationsLoading: loading,
      canFetchMore: canFetchMore,
      fetchMoreNotifications: fetchMoreNotifications,
      resetNotifications: resetNotifications,
      notificationChannels: updatedNotificationChannels,
      channelList: channelList,
      totalNotificationsCount: totalNotificationsCount,
    }
  }, [
    updatedNotifications,
    loading,
    canFetchMore,
    fetchMoreNotifications,
    resetNotifications,
    updatedNotificationChannels,
    channelList,
    totalNotificationsCount,
  ])

  return <NotificationsContext.Provider value={contextValue}>{children}</NotificationsContext.Provider>
}

export const useNotifications = () => {
  const notificationsContext = useContext(NotificationsContext)

  return {
    ...notificationsContext,
  }
}
